import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-slate-500">{k}</span><span className="font-medium text-right">{v}</span>
  </div>
);

const Shell = ({ children }) => (
  <div className="min-h-[75vh] flex items-center justify-center px-4 py-12">
    <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-6 sm:p-8 space-y-5" data-testid="approve-page">{children}</div>
  </div>
);

export default function ApprovePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const q = useQuery({ queryKey: ["approve-link", token], queryFn: async () => (await axios.get(`${API}/approve-link`, { params: { token } })).data, enabled: !!token, retry: false });

  const approve = async () => {
    setBusy(true);
    setError("");
    try { setResult((await axios.post(`${API}/approve-link`, { token })).data); }
    catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!token || q.isError) return (
    <Shell>
      <AlertTriangle className="h-10 w-10 text-amber-500" />
      <p className="font-display font-semibold text-lg" data-testid="approve-error">{token ? errMsg(q.error) : "This approval link is not valid."}</p>
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF6B00]" data-testid="approve-admin-link">Open the admin panel <ArrowRight className="h-4 w-4" /></Link>
    </Shell>
  );
  if (q.isLoading) return <Shell><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></Shell>;

  const s = result || q.data;
  return (
    <Shell>
      <div className="space-y-2">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF6B00]">Approve booking</p>
        <p className="font-mono font-bold tracking-wider text-2xl text-[#0A192F]" data-testid="approve-tracking-number">{s.tracking_number}</p>
        <StatusBadge status={s.status} testId="approve-status" />
      </div>
      <div>
        <Row k="Route" v={`${s.origin} → ${s.destination}`} />
        <Row k="Sender" v={s.sender} />
        <Row k="Receiver" v={s.receiver} />
        <Row k="Contents" v={`${s.contents} · ${s.weight_kg} kg`} />
        <Row k="Shipping method" v={s.service} />
        {s.estimated_cost != null && <Row k="Estimated cost" v={`$${Number(s.estimated_cost).toFixed(2)}`} />}
      </div>
      {result?.approved_now ? (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-sm p-4" data-testid="approve-success">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
          <p className="text-sm text-emerald-900">Approved. Tracking has started{result.email === "sent" ? " and the receiver was emailed" : ""}.</p>
        </div>
      ) : s.can_approve ? (
        <button onClick={approve} disabled={busy} className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg rounded-sm flex items-center justify-center gap-2 transition-colors" data-testid="approve-confirm-btn">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Approve shipment
        </button>
      ) : (
        <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-4" data-testid="approve-not-needed">
          {s.status === "Pending Approval" ? "This approval link was already used. Approve it from the admin panel." : "This shipment is already approved, so there is nothing to do."}
        </p>
      )}
      {error && <p className="text-sm text-red-600" data-testid="approve-action-error">{error}</p>}
      <div className="flex flex-wrap gap-4 text-sm font-semibold">
        <Link to={`/track/${s.tracking_number}`} className="text-[#0A192F] hover:text-[#FF6B00]" data-testid="approve-track-link">View tracking page</Link>
        <Link to="/admin" className="text-[#FF6B00]" data-testid="approve-admin-panel-link">Admin panel</Link>
      </div>
    </Shell>
  );
}
